import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';

export const useCart = () => {
  const [cart, setCart] = useState(() => {
    // Cart Persistence: Load from localStorage on init
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  
  // Loading states for different operations
  const [loadingStates, setLoadingStates] = useState({
    addingItem: false,
    removingItem: false,
    updatingQuantity: false,
    syncing: false
  });

  // Persist cart changes to localStorage
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
    
    // Optional: Sync with backend if user is logged in
    if (cart.length > 0) {
      syncCartWithBackend(cart);
    }
  }, [cart]);

  const syncCartWithBackend = async (cartData) => {
    // Only attempt to sync if we're in production
    if (process.env.NODE_ENV === 'production') {
      setLoadingStates(prev => ({ ...prev, syncing: true }));
      try {
        await fetch('/api/cart/sync', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: cartData })
        });
      } catch (err) {
        console.error('Failed to sync cart:', err);
        // Will retry on next cart update
      } finally {
        setLoadingStates(prev => ({ ...prev, syncing: false }));
      }
    }
  };

  const addToCart = useCallback(async (product) => {
    setLoadingStates(prev => ({ ...prev, addingItem: true }));
    try {
      // Check if item already exists
      const existingItem = cart.find(item => item.id === product.id);
      if (existingItem) {
        toast.info('Item already in cart');
        return;
      }

      const newItem = { ...product, addedAt: Date.now() };
      setCart(prev => [...prev, newItem]);
      toast.success('Added to cart');
    } catch (err) {
      toast.error('Failed to add item');
      console.error('Add to cart error:', err);
    } finally {
      setLoadingStates(prev => ({ ...prev, addingItem: false }));
    }
  }, [cart]);

  const updateQuantity = useCallback(async (productId, newQuantity) => {
    setLoadingStates(prev => ({ ...prev, updatingQuantity: true }));
    try {
      setCart(prev => prev.map(item => 
        item.id === productId ? { ...item, quantity: newQuantity } : item
      ));
    } catch (err) {
      toast.error('Failed to update quantity');
    } finally {
      setLoadingStates(prev => ({ ...prev, updatingQuantity: false }));
    }
  }, []);

  const removeFromCart = useCallback(async (productId) => {
    setLoadingStates(prev => ({ ...prev, removingItem: true }));
    try {
      setCart(prev => prev.filter(item => item.id !== productId));
      toast.info('Item removed from cart');
    } catch (err) {
      toast.error('Failed to remove item');
    } finally {
      setLoadingStates(prev => ({ ...prev, removingItem: false }));
    }
  }, []);

  return {
    cart,
    loadingStates,
    addToCart,
    updateQuantity,
    removeFromCart,
    isInCart: useCallback((id) => cart.some(item => item.id === id), [cart])
  };
}; 